import type {
  GoogleNativeReportResponse,
  MetaNativeReportResponse,
} from '@/types/generated/endpoints/native_reports';

export const GOOGLE_NATIVE_REPORT_TYPES = [
  'account',
  'campaigns',
  'search_terms',
  'quality_score',
  'ads',
  'product_partitions',
  'bidding_strategies',
  'device',
  'geo',
  'daypart',
  'weekday',
  'age',
  'gender',
] as const;

export const META_NATIVE_REPORT_TYPES = [
  'creative',
  'placement',
  'device',
  'age',
  'gender',
  'daypart',
] as const;

export type GoogleNativeReportType =
  (typeof GOOGLE_NATIVE_REPORT_TYPES)[number];
export type MetaNativeReportType = (typeof META_NATIVE_REPORT_TYPES)[number];

// A report type the backend serves but the lists above omit resolves to something other than
// `never` here, so it fails to compile rather than vanishing from the selector.
type AssertAllServedTypesAreListed<T extends never> = T;
/** @public */
export type GoogleNativeReportTypesAreComplete = AssertAllServedTypesAreListed<
  Exclude<GoogleNativeReportResponse['report_type'], GoogleNativeReportType>
>;
/** @public */
export type MetaNativeReportTypesAreComplete = AssertAllServedTypesAreListed<
  Exclude<MetaNativeReportResponse['report_type'], MetaNativeReportType>
>;

// One key per platform and type: label lookup and per-table display preferences share it.
export const nativeReportKey = (
  report: Pick<NativeReportResponse, 'platform' | 'report_type'>,
): string => `${report.platform}_${report.report_type}`;

// Namespaced so one `report_type` search param addresses curated and native routes alike.
export const GOOGLE_NATIVE_IDS = GOOGLE_NATIVE_REPORT_TYPES.map(
  (t) => `google_${t}` as const,
);
// Offered per org (product groups need Shopping); until the backend answers, those stay hidden.
const ORG_GATED_NATIVE_TYPES: ReadonlySet<GoogleNativeReportType> = new Set([
  'product_partitions',
]);
export const offeredGoogleNativeIds = (
  offered: readonly GoogleNativeReportType[] | undefined,
) =>
  GOOGLE_NATIVE_REPORT_TYPES.filter((type) =>
    offered ? offered.includes(type) : !ORG_GATED_NATIVE_TYPES.has(type),
  ).map((type) => `google_${type}` as const);
export const META_NATIVE_IDS = META_NATIVE_REPORT_TYPES.map(
  (t) => `meta_${t}` as const,
);
export const NATIVE_REPORT_IDS = [
  ...GOOGLE_NATIVE_IDS,
  ...META_NATIVE_IDS,
] as const;

export type NativeReportId = (typeof NATIVE_REPORT_IDS)[number];

export type NativeReportTarget =
  | { platform: 'google'; reportType: GoogleNativeReportType }
  | { platform: 'meta'; reportType: MetaNativeReportType };

const NATIVE_ID_SET: ReadonlySet<string> = new Set(NATIVE_REPORT_IDS);

export const isNativeReportId = (value: string): value is NativeReportId =>
  NATIVE_ID_SET.has(value);

// Splits at the platform prefix only, so `google_search_terms` keeps its `_` intact.
export const nativeReportTarget = (id: NativeReportId): NativeReportTarget => {
  if (id.startsWith('google_')) {
    return {
      platform: 'google',
      reportType: id.slice('google_'.length) as GoogleNativeReportType,
    };
  }
  return {
    platform: 'meta',
    reportType: id.slice('meta_'.length) as MetaNativeReportType,
  };
};

export type NativeReportResponse =
  | GoogleNativeReportResponse
  | MetaNativeReportResponse;

const NATIVE_RESPONSE_REPORT_TYPES: ReadonlySet<string> = new Set([
  ...GOOGLE_NATIVE_REPORT_TYPES,
  ...META_NATIVE_REPORT_TYPES,
]);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// Same discriminant contract as `isReportResponse`, for the native report union.
export const isNativeReportResponse = (
  data: unknown,
): data is NativeReportResponse =>
  isRecord(data) &&
  typeof data.report_type === 'string' &&
  NATIVE_RESPONSE_REPORT_TYPES.has(data.report_type);
